import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionIcon, Button, Center, Group, Loader, Paper, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import { IconChecklist, IconFilePlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { Healthcare, Security, Care } from '@risrx/icons';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { clearSessionExpiry, logoutSession } from '../api/auth';
import { listDrafts, deleteDraft } from '../api/result';
import { ApiError } from '../api/http';
import PageShell from '../components/PageShell';
import { useProtectedSession } from '../hooks/useProtectedSession';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import NotFoundPage from './NotFoundPage';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LandingIcon = React.ComponentType<any>;

// Add RIS Rx icons here as they are used in JSONC configs (key = JSONC "icon" value)
const LANDING_ICONS: Record<string, LandingIcon> = {
  // Tabler (existing)
  'clipboard-plus': IconChecklist,
  'file-plus': IconFilePlus,
  'search': IconSearch,
  // RIS Rx brand icons
  'healthcare': Healthcare,
  'security': Security,
  'care': Care,
};

type ActionItem = {
  to: string;
  label: string;
  labelSize?: string;
  description: string;
  color?: string;
  icon?: string;
  iconColor?: string;
  iconVariant?: 'plain' | 'filled';
  backgroundColor?: string;
  buttonLabel?: string;
  buttonVariant: 'filled' | 'outline';
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  buttonWidth?: string;
  buttonHoverColor?: string;
};

type ActionDefaults = Required<Pick<ActionItem, 'label' | 'description' | 'icon' | 'iconVariant' | 'buttonLabel' | 'buttonVariant'>>;

const LANDING_DEFAULTS: Record<'enrollments' | 'escalations' | 'escalationsHistory', ActionDefaults> = {
  enrollments: {
    label: 'Copay Enrollment',
    description: 'Submit a new copay enrollment for a commercially insured patient.',
    icon: 'clipboard-plus',
    iconVariant: 'filled',
    buttonLabel: 'Start enrollment submission →',
    buttonVariant: 'filled',
  },
  escalations: {
    label: 'Submit New Escalation',
    description: 'Submit an escalation request for an existing benefits investigation.',
    icon: 'clipboard-plus',
    iconVariant: 'plain',
    buttonLabel: 'Start escalation submission →',
    buttonVariant: 'outline',
  },
  escalationsHistory: {
    label: 'Track Escalation',
    description: 'Review prior escalations and open in-flight cases.',
    icon: 'search',
    iconVariant: 'plain',
    buttonLabel: 'Open tracker →',
    buttonVariant: 'outline',
  },
};

export default function EscalationPortalPage() {
  const navigate = useNavigate();
  const { data: config, error, isLoading, sessionScopeKey } = useResolvedProgram();
  const sessionQuery = useProtectedSession(sessionScopeKey);
  const logoutMutation = useMutation({ mutationFn: logoutSession });
  const queryClient = useQueryClient();
  const deleteDraftMutation = useMutation({
    mutationFn: (draftId: string) => deleteDraft('escalations', draftId),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['drafts', 'escalations'] }); },
  });

  const hasEscalations = Boolean(config?.escalations);

  const draftsQuery = useQuery({
    queryKey: ['drafts', 'escalations'],
    queryFn: () => listDrafts('escalations'),
    enabled: hasEscalations
      && config?.solution === 'escalationiq'
      && !sessionQuery.isUnauthorized
      && Boolean(sessionQuery.data),
    refetchOnMount: 'always',
  });

  if (isLoading || sessionQuery.isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error instanceof ApiError && error.status === 404) {
    return <NotFoundPage />;
  }

  if (sessionQuery.isUnauthorized) {
    return <Navigate to="/auth/request" state={{ returnTo: '/' }} replace />;
  }

  if (!config) {
    return <Navigate to="/auth/request" state={{ returnTo: '/' }} replace />;
  }

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      if (sessionScopeKey) {
        clearSessionExpiry(sessionScopeKey);
      }
      navigate('/auth/request', { replace: true });
    }
  };

  const hasEnrollments = Boolean(config.enrollments);
  const hasHistory = config.solution === 'escalationiq';
  const sessionEmail = sessionQuery.data?.email;

  const landing = config.pages?.landing;

  const actions: ActionItem[] = [
    hasEnrollments ? {
      to: '/enrollments',
      label: landing?.enrollments?.label ?? LANDING_DEFAULTS.enrollments.label,
      labelSize: landing?.enrollments?.labelSize,
      description: landing?.enrollments?.description ?? LANDING_DEFAULTS.enrollments.description,
      color: landing?.enrollments?.color,
      icon: landing?.enrollments?.icon ?? LANDING_DEFAULTS.enrollments.icon,
      iconColor: landing?.enrollments?.iconColor,
      iconVariant: landing?.enrollments?.iconVariant ?? LANDING_DEFAULTS.enrollments.iconVariant,
      backgroundColor: landing?.enrollments?.backgroundColor,
      buttonLabel: landing?.enrollments?.buttonLabel ?? LANDING_DEFAULTS.enrollments.buttonLabel,
      buttonVariant: landing?.enrollments?.buttonVariant ?? LANDING_DEFAULTS.enrollments.buttonVariant,
      buttonSize: landing?.enrollments?.buttonSize,
      buttonWidth: landing?.enrollments?.buttonWidth,
      buttonHoverColor: landing?.enrollments?.buttonHoverColor,
    } : null,
    hasHistory ? {
      to: '/escalations/history',
      label: landing?.escalationsHistory?.label ?? LANDING_DEFAULTS.escalationsHistory.label,
      labelSize: landing?.escalationsHistory?.labelSize,
      description: landing?.escalationsHistory?.description ?? LANDING_DEFAULTS.escalationsHistory.description,
      color: landing?.escalationsHistory?.color,
      icon: landing?.escalationsHistory?.icon ?? LANDING_DEFAULTS.escalationsHistory.icon,
      iconColor: landing?.escalationsHistory?.iconColor,
      iconVariant: landing?.escalationsHistory?.iconVariant ?? LANDING_DEFAULTS.escalationsHistory.iconVariant,
      backgroundColor: landing?.escalationsHistory?.backgroundColor,
      buttonLabel: landing?.escalationsHistory?.buttonLabel ?? LANDING_DEFAULTS.escalationsHistory.buttonLabel,
      buttonVariant: landing?.escalationsHistory?.buttonVariant ?? LANDING_DEFAULTS.escalationsHistory.buttonVariant,
      buttonSize: landing?.escalationsHistory?.buttonSize,
      buttonWidth: landing?.escalationsHistory?.buttonWidth,
      buttonHoverColor: landing?.escalationsHistory?.buttonHoverColor,
    } : null,
    hasEscalations ? {
      to: '/escalations',
      label: landing?.escalations?.label ?? LANDING_DEFAULTS.escalations.label,
      labelSize: landing?.escalations?.labelSize,
      description: landing?.escalations?.description ?? LANDING_DEFAULTS.escalations.description,
      color: landing?.escalations?.color,
      icon: landing?.escalations?.icon ?? LANDING_DEFAULTS.escalations.icon,
      iconColor: landing?.escalations?.iconColor,
      iconVariant: landing?.escalations?.iconVariant ?? LANDING_DEFAULTS.escalations.iconVariant,
      backgroundColor: landing?.escalations?.backgroundColor,
      buttonLabel: landing?.escalations?.buttonLabel ?? LANDING_DEFAULTS.escalations.buttonLabel,
      buttonVariant: landing?.escalations?.buttonVariant ?? LANDING_DEFAULTS.escalations.buttonVariant,
      buttonSize: landing?.escalations?.buttonSize,
      buttonWidth: landing?.escalations?.buttonWidth,
      buttonHoverColor: landing?.escalations?.buttonHoverColor,
    } : null,
  ].filter(Boolean) as ActionItem[];

  // Auto-redirect when only one feature is available for this program
  if (actions.length === 1) {
    return <Navigate to={actions[0].to} replace />;
  }

  return (
    <PageShell
      config={config}
      headerLogoUrl={landing?.logoUrl ?? config.theme.logoUrl}
      headerTitle={landing?.title ?? config.theme.displayName}
      headerSubtitle={landing?.subtitle}
      headerTitleOrder={1}
      sessionEmail={sessionEmail}
      onLogout={() => { void handleLogout(); }}
      isLoggingOut={logoutMutation.isPending}
      containerSize="lg"
      support={landing?.showSupportInfo === true ? config.support : undefined}
    >
      <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
        {actions.map((action) => {
          const IconComponent = action.icon ? LANDING_ICONS[action.icon] : null;
          return (
            <Link key={action.to} to={action.to} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', height: '100%' }}>
              <Paper p="xl" withBorder radius="md" style={{ cursor: 'pointer', width: '100%', background: action.backgroundColor }} className="portal-hero-card">
                <Stack gap="sm" style={{ height: '100%' }}>
                  {IconComponent && (
                    action.iconVariant === 'filled' ? (
                      <div style={{ background: action.iconColor ?? action.color ?? 'var(--primary-color)', borderRadius: 8, padding: 10, display: 'inline-flex', alignSelf: 'flex-start' }}>
                        <IconComponent size={32} stroke={1.5} color="white" />
                      </div>
                    ) : (
                      <IconComponent size={48} stroke={1.5} style={{ color: action.iconColor ?? action.color ?? 'var(--primary-color)' }} />
                    )
                  )}
                  <Text fw={700} size={(action.labelSize ?? 'lg') as never} style={{ color: action.color ?? 'var(--primary-color)' }}>{action.label}</Text>
                  <Text size="sm" c="dimmed">{action.description}</Text>
                  {action.buttonLabel && (
                    <Button
                      component="span"
                      mt="auto"
                      size={action.buttonSize ?? 'md'}
                      style={{
                        width: action.buttonWidth ?? '100%',
                        ...(action.buttonHoverColor ? { '--portal-btn-hover-bg': action.buttonHoverColor } : {}),
                      } as React.CSSProperties}
                      variant={action.buttonVariant === 'outline' ? 'default' : 'filled'}
                      className={action.buttonVariant !== 'outline' ? 'portal-primary-button' : 'portal-outline-button'}
                    >
                      {action.buttonLabel}
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Link>
          );
        })}
      </SimpleGrid>

      {hasEscalations && draftsQuery.isSuccess && draftsQuery.data.length > 0 && (
        <Stack gap="md">
          <div>
            <Text fw={600} size="lg" style={{paddingTop: 'var(--mantine-spacing-xl)'}}>{landing?.drafts?.title ?? 'Saved Drafts'}</Text>
            {landing?.drafts?.description && (
              <Text size="xs" c="dimmed">{landing.drafts.description}</Text>
            )}
          </div>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
            {draftsQuery.data.map((draft) => (
                <Paper
                  key={draft.resultId}
                  p="md"
                  withBorder
                  radius="md"
                  style={{ cursor: 'pointer' }}
                  className="portal-link-card"
                  onClick={() => navigate('/escalations', { state: { resumeDraftId: draft.resultId } })}
                >
                  <Stack gap="xs">
                    <Group justify="space-between" align="center">
                      <Text fw={600} c="dimmed" size="sm">
                        {draft.cardholderId ? draft.cardholderId : 'Escalation draft'}
                      </Text>
                      <Group gap="xs">
                        <Tooltip label="Delete draft" withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            loading={deleteDraftMutation.isPending && deleteDraftMutation.variables === draft.resultId}
                            onClick={(e) => { e.stopPropagation(); deleteDraftMutation.mutate(draft.resultId); }}
                            aria-label="Delete draft"
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Text c="dimmed">›</Text>
                      </Group>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Last saved {new Date(draft.updatedAt).toLocaleString()}
                    </Text>
                  </Stack>
                </Paper>
              ))
            }
            </SimpleGrid>
        </Stack>
      )}
    </PageShell>
  );
}
